<!-- TODO: translate! -->
<template>
  <div class="dsd-item dv-preview-container">
    <h2
      class="mb-lg-4 mt-lg-4"
      data-toggle="tooltip"
      data-placement="top"
      data-cy="dataset-distribution-preview"
    >
      Preview of <em> {{ title }} </em>
    </h2>
    <div v-if="loading">
      <div class="d-flex" style="position: relative; height: 150px;">
        <div class="ecl-spinner ecl-spinner--primary ecl-spinner--m ecl-spinner--centered ecl-spinner--visible" role="alert">
          <svg class="ecl-spinner__loader" viewBox="25 25 50 50">
            <circle class="ecl-spinner__circle" cx="50" cy="50" r="20" fill="none" stroke-width="4px" stroke-miterlimit="10"
              vector-effect="non-scaling-stroke" />
          </svg>
          <div class="ecl-spinner__text">Loading</div>
        </div>
      </div>
    </div>
    <div v-else-if="isPreviewableDirectly" class="dv-direct-preview-container">
      <div v-if="directPreviewLoading" class="d-flex" style="position: relative; height: 150px;">
        <div class="ecl-spinner ecl-spinner--primary ecl-spinner--m ecl-spinner--centered ecl-spinner--visible" role="alert">
          <svg class="ecl-spinner__loader" viewBox="25 25 50 50">
            <circle class="ecl-spinner__circle" cx="50" cy="50" r="20" fill="none" stroke-width="4px" stroke-miterlimit="10"
              vector-effect="non-scaling-stroke" />
          </svg>
          <div class="ecl-spinner__text">Loading</div>
        </div>
      </div>
      <iframe
        v-if="viewerUrl"
        :src="viewerUrl"
        width="100%"
        height="700"
        class="dv-direct-preview-frame"
        title="Distribution file preview"
        @load="onDirectPreviewLoaded"
        @error="onDirectPreviewError"
        v-show="!directPreviewLoading"
      ></iframe>
      <div v-else-if="!directPreviewLoading" class="dsd-banners">
        <ECInfo type="info" title="Details:" message="The source file preview is currently inaccessible">
          We're sorry, but previewing this file is currently not possible.
          <br v-if="directPreviewNotice" />
          <span v-if="directPreviewNotice">{{ directPreviewNotice }}</span>
          <br />
          <a v-if="googleViewerFallbackUrl" :href="googleViewerFallbackUrl" target="_blank" rel="noopener noreferrer">Open preview via Google Viewer</a>
          <br v-if="googleViewerFallbackUrl" />
          <a :href="downloadUrl" target="_blank" rel="noopener noreferrer">Open file in a new tab</a>
        </ECInfo>
      </div>
    </div>
    <div v-else-if="invalidFile" class="dsd-banners">
      <!-- <ECInfo type="info" title="Details:" :message="errorMessage"> -->
      <ECInfo
        type="info"
        title="Details:"
        message="The source file is currently inaccessible"
      >
        We're sorry, but previewing this file is currently not possible.
        <br />
        <br />
      </ECInfo>
    </div>

    <!-- Card -->
    <div v-else>
      <div v-if="this.jsonData" class="dv-card card text-center">
        <!-- Card Header (Navigation bar) -->
        <dv-nav-bar @showTab="showTab" :activeTab="activeTab"></dv-nav-bar>

        <!-- Card Body -->
        <div class="card-body">
            <!-- Tab for Table View -->
          <dv-tabular-view
            v-if="activeTab == 'table-view'"
            role="tabpanel"
            id="dv-table-view"
            :columnTitles="allLabels"
            :rowData="dataRows"
          >
          </dv-tabular-view>
          <!-- Tab for Numerical Charts -->
          <dv-numerical-view
            v-else-if="activeTab == 'numerical'"
            role="tabpanel"
            :showNumTabContent="showNumTabContent"
            :categoricalLabels="categoricalLabels"
            :numericalLabels="numericalLabels"
            :xPicked="xPicked"
            :yPicked="yPicked"
            :viewType="viewType"
            :viewTypes="viewOptions.map((e) => e.label)"
            :chartData="dataCollection"
            :chartOptions="options"
            :printChartAsPDF="printChartAsPDF"
            @updateXPicked="updateXPicked"
            @updateYPicked="updateYPicked"
            @removeChart="removeChart"
          />
          <!-- Tab for Categorical Charts -->
          <dv-categorical-view
            v-else-if="activeTab == 'categorical'"
            role="tabpanel"
            :showCatTabContent="showCatTabContent"
            :labels="categoricalLabels"
            :yPicked="catYPicked"
            :viewType="catViewType"
            :viewTypes="catViewOptions.map((e) => e.label)"
            :chartData="catDataCollection"
            :chartOptions="options"
            :printChartAsPDF="printChartAsPDF"
            @updateYPicked="updateCatYPicked"
            @removeChart="removeChart"
          />
        </div>
      </div>

      <!-- Records range bar -->
      <div class="dv-records-range-container text-right">
        <small class="dv-records-range-container-record-number mr-1"
          >{{ this.dataRows.length }} Records</small
        >
        <small v-if="activeTab === 'numerical' && showNumTabContent">
          <label for="from">show records from</label>
          <input
            @change="updateRange"
            type="number"
            id="from"
            v-model.number="fromIndex"
            min="1"
          />
          <label for="to">-</label>
          <input
            @change="updateRange"
            type="number"
            id="to"
            v-model.number="toIndex"
            :min="fromIndex"
            :max="this.dataRows.length"
          />
        </small>
      </div>

      <br />

      <!-- Feedback Tool -->
      <dv-feedback-tool
        :downloadUrl="downloadUrl"
        :allChartOptions="allChartOptions"
        :allLabels="allLabels"
        :numericalLabels="numericalLabels"
        :categoricalLabels="categoricalLabels"
        :defaultSuggestedChartType="defaultSuggestedChartType"
        :defaultSuggestedX="defaultSuggestedX"
        :defaultSuggestedY="defaultSuggestedY"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import ECInfo from "../ECInfo.vue"
import DvNavBar from "./DvNavBar.vue";
import DvNumericalView from './DvNumericalView.vue';
import DvCategoricalView from "./DvCategoricalView.vue";
import DvTabularView from "./DvTabularView.vue";
import DvFeedbackTool from "./DvFeedbackTool.vue";
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export default {
  name: "DistributionVisualisation",
  components: {
    ECInfo,
    DvNavBar,
    DvNumericalView,
    DvCategoricalView,
    DvTabularView,
    DvFeedbackTool,
  },
  props: {
    title: String,
    downloadUrl: String,
    format: String,
    compressFormat: String,
  },
  data() {
    return {
      // print: false,
      invalidFile: null,
      loading: true,
      directPreviewLoading: true,
      directPreviewUrl: '',
      directPreviewLoadTimeoutId: null,
      directPreviewInitTimeoutId: null,
      directPreviewNotice: '',
      googleViewerFallbackUrl: '',
      errorMessage: '',
      jsonData: null,
      activeTab: "",
      allLabels: [],
      categoricalLabels: [],
      numericalLabels: [],
      fromIndex: 1,
      toIndex: null,
      viewOptions: [
        { value: "Bar", label: "Bar" },
        { value: "Line", label: "Line" },
      ],
      showNumTabContent: true,
      chartLabels: [],
      viewType: "",
      xPicked: "",
      yPicked: [], // multiple y labels can be picked --> Array of strings
      xLabels: [],
      dataCollection: {},
      datasets: [], // dataRows, label, backgroundColor
      dataRows: [], // only the data arrays
      hideXLabels: false,

      catViewOptions: [
        { value: "Bar", label: "Bar" },
        // { value: "Pie", label:"Pie" },
        // { value: "Line", label:"Line" },
        { value: "Doughnut", label: "Doughnut" },
      ],
      showCatTabContent: true,
      nCatToShow: 100, // TODO: tell that not all categories are shown (in tooltip for example)
      catChartValues: [],
      catYValues: [],
      catViewType: "",
      catYPicked: "", // only Y, because X will be automatically generated from the categories of Y
      catDataCollection: {},
      catDatasets: [], // dataRows, label, backgroundColor
      categorizedData: [],

      currentPage: 1,
      totalPages: 0,
      // Feedback tool
      allChartOptions: ["Line", "Bar", "Doughnut"],
      defaultSuggestedChartType: "",
      defaultSuggestedX: "",
      defaultSuggestedY: "",
    };
  },
  computed: {
    options() {
      return {
        responsive: true,
        scales: {
          x: {
            ticks: {
              maxTicksLimit: 20,
            },
            title: {
              display: true,
              text: this.xPicked,
              font: {
                size: 14,
                weight: 'bold',
              },
            },
          },
        },
        elements: {
          point: {
            radius: 0,
            // pointStyle: 'line',
          }
        },
        plugins: {
          title: {
            display: true,
            text: this.title,
            font: {
              size: 18,
              weight: 'bold',
            },
          },
        },
      };
    },
    isPreviewableDirectly() {
      if (typeof this.format !== 'string') return false;

      const fileFormat = this.format.trim().toLowerCase();
      const normalizedFormat = fileFormat.replace(/^https?:\/\/www\.iana\.org\/assignments\/media-types\//, '');

      const previewableFormats = [
        'pdf',
        'odf',
        'ods',
        'odt',
        'doc',
        'docx',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.oasis.opendocument.text',
        'application/vnd.oasis.opendocument.spreadsheet',
      ];

      return previewableFormats.includes(fileFormat) || previewableFormats.includes(normalizedFormat);
    },
    viewerUrl() {
      return this.directPreviewUrl;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    clearDirectPreviewLoadTimeout() {
      if (this.directPreviewLoadTimeoutId) {
        window.clearTimeout(this.directPreviewLoadTimeoutId);
      }
      this.directPreviewLoadTimeoutId = null;
    },
    clearDirectPreviewInitTimeout() {
      if (this.directPreviewInitTimeoutId) {
        window.clearTimeout(this.directPreviewInitTimeoutId);
      }
      this.directPreviewInitTimeoutId = null;
    },
    scheduleDirectPreviewInitTimeout() {
      this.clearDirectPreviewInitTimeout();
      this.directPreviewInitTimeoutId = window.setTimeout(() => {
        if (!this.directPreviewLoading) return;
        this.directPreviewLoading = false;
        this.directPreviewUrl = '';
        this.googleViewerFallbackUrl = this.buildGoogleViewerUrl(this.downloadUrl);
        this.directPreviewNotice = 'Preview request timed out. Please open the file in a new tab or use Google Viewer.';
      }, 12000);
    },
    scheduleDirectPreviewLoadTimeout() {
      this.clearDirectPreviewLoadTimeout();
      this.directPreviewLoadTimeoutId = window.setTimeout(() => {
        if (!this.directPreviewLoading) return;
        this.directPreviewLoading = false;
        this.directPreviewUrl = '';
      }, 12000);
    },
    getPreviewHttpClients() {
      const clients = [];

      if (this.$corsproxyService && typeof this.$corsproxyService.get === 'function') {
        clients.push(this.$corsproxyService);
      }

      if (this.$bulkDownloadAxiosInstance && typeof this.$bulkDownloadAxiosInstance.get === 'function') {
        clients.push(this.$bulkDownloadAxiosInstance);
      }

      clients.push(axios);
      return clients;
    },
    isPdfLikeFormat() {
      if (typeof this.format !== 'string') return false;

      const fileFormat = this.format.trim().toLowerCase();
      const normalizedFormat = fileFormat.replace(/^https?:\/\/www\.iana\.org\/assignments\/media-types\//, '');
      return ['pdf', 'application/pdf'].includes(fileFormat) || ['pdf', 'application/pdf'].includes(normalizedFormat);
    },
    buildGoogleViewerUrl(url) {
      if (typeof url !== 'string' || !url.trim()) return '';
      return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url.trim())}`;
    },
    async initDirectPreview() {
      this.directPreviewNotice = '';
      this.googleViewerFallbackUrl = '';
      this.clearDirectPreviewInitTimeout();

      if (!this.isPdfLikeFormat()) {
        this.directPreviewUrl = this.downloadUrl;
        this.scheduleDirectPreviewLoadTimeout();
        return;
      }

      this.scheduleDirectPreviewInitTimeout();

      let lastError = null;

      for (const httpClient of this.getPreviewHttpClients()) {
        try {
          const response = await httpClient.get(this.downloadUrl, {
            responseType: 'blob',
            timeout: 7000,
          });
          const blob = new Blob([response.data], { type: 'application/pdf' });
          this.directPreviewUrl = URL.createObjectURL(blob);
          this.clearDirectPreviewInitTimeout();
          this.scheduleDirectPreviewLoadTimeout();
          return;
        } catch (err) {
          lastError = err;
        }
      }

      this.clearDirectPreviewInitTimeout();

      if (lastError) {
        console.error('ERROR', lastError.message);
        this.errorMessage = lastError.message;
      }

      // Temporary release fallback: use Google Viewer when direct/proxy blob preview fails.
      const googleViewerUrl = this.buildGoogleViewerUrl(this.downloadUrl);
      if (googleViewerUrl) {
        this.directPreviewUrl = '';
        this.googleViewerFallbackUrl = googleViewerUrl;
        this.directPreviewLoading = false;
        this.directPreviewNotice = 'Inline preview failed. You can open this file via Google Viewer in a new tab.';
        return;
      }

      this.directPreviewUrl = '';
      this.directPreviewLoading = false;
      this.directPreviewNotice = 'This source appears to force a file download instead of allowing inline preview.';
    },
    cleanupDirectPreviewUrl() {
      this.clearDirectPreviewLoadTimeout();
      this.clearDirectPreviewInitTimeout();
      if (this.directPreviewUrl && this.directPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.directPreviewUrl);
      }
      this.directPreviewUrl = '';
      this.directPreviewNotice = '';
      this.googleViewerFallbackUrl = '';
    },
    fetchData() {
      if (this.isPreviewableDirectly) {
        this.loading = false;
        this.invalidFile = false;
        this.directPreviewLoading = true;
        this.cleanupDirectPreviewUrl();
        this.initDirectPreview();
        return;
      }

      let params = {
        urlString: this.downloadUrl,
        fileFormat: this.format,
      }
      if(this.compressFormat && typeof this.compressFormat === 'string' && this.compressFormat.trim() !== '') {
        params.compressFormat = this.compressFormat
        const regex = /https?:\/\/www\.iana\.org\/assignments\/media-types\/(.*\/.*)/;
        const match = params.compressFormat.match(regex);
        if (match) {
          params.compressFormat = match[1];
        }
      }
      axios
        .get(
          `${this.$env.content.datasetDetails.distributions.visualisationServiceUrl}/dataPreview`,
          {
            params: params,
          }
        )
        .then((response) => {
          this.jsonData = response.data;
          this.invalidFile = false;
          this.addData();
          this.loading = false;
        })
        .catch((err) => {
          console.error('ERROR', err.message);
          this.loading = false;
          this.invalidFile = true;
          this.errorMessage = err.message;
        });
    },
    showTab(tabName) {
      this.activeTab = tabName;
    },
    // Once the JSON record is fetched, this method is called to fill all the data() fields with the necessary data
    addData() {
      this.activeTab = 'table-view';
      this.allLabels = this.jsonData.labels.all_labels;
      this.numericalLabels = this.jsonData.labels.numerical_labels;
      if (this.numericalLabels.length == 0) this.showNumTabContent = false;
      this.categoricalLabels = this.jsonData.labels.categorical_labels;
      if (this.categoricalLabels.length == 0) {
        this.showCatTabContent = false;
        // this.showXLabels = false;
      }
      this.viewType = this.jsonData.default_view_options.default_numerical_chart;
      this.xPicked = this.jsonData.default_view_options.default_axes.numerical_chart.x;
      this.yPicked = this.jsonData.default_view_options.default_axes.numerical_chart.y;

      this.catViewType = this.jsonData.default_view_options.default_categorical_chart;
      this.catYPicked = this.jsonData.default_view_options.default_axes.categorical_chart;
      this.dataRows = this.jsonData.data; // this line can take time
      if (!this.toIndex)
        this.toIndex = this.dataRows.length < 100 ? this.dataRows.length : 100;
      this.categorizedData = this.jsonData.categorized;

      let yValues = this.dataRows
        .map((e) => e[this.yPicked[0]])
        .slice(this.fromIndex - 1, this.toIndex);
      this.datasets[0] = {
        label: this.yPicked[0],
        data: yValues,
        backgroundColor: "#001D85",
        borderColor: "#001D85",
        borderWidth: 1,
      };

      this.defaultSuggestedChartType = this.allChartOptions[0];
      this.defaultSuggestedX = this.categoricalLabels[0];
      this.defaultSuggestedY = this.numericalLabels[0] ? [this.numericalLabels[0]] : [];
    },
    fillNumPlotData() {
      // In case there are no assigned lables, create empty lables (chart.js needs an array of lables, otherwise empty chart will display)
      if (this.xLabels.length === 0 || this.hideXLabels) {
        let emptyValues = Array(this.datasets[0].data.length).fill("");
        this.dataCollection = {
          labels: emptyValues,
          datasets: structuredClone((this.datasets.value)),
        };
      } else {
        let tmpDatasets = JSON.parse(JSON.stringify(this.datasets));
        for (let i = 0; i < tmpDatasets.length; i++) {
          tmpDatasets[i].data = this.datasets[i].data.slice(
            this.fromIndex - 1,
            this.toIndex
          );
        }
        this.xLabels = this.chartLabels.slice(this.fromIndex - 1, this.toIndex);
        this.dataCollection = {
          labels: this.xLabels,
          datasets: tmpDatasets,
        };
      }
    },
    fillCatPlotData() {
      this.catChartValues = Object.keys(
        this.categorizedData.find((e) => e.column === this.catYPicked)
          .categories
      ).slice(0, this.nCatToShow); // get array of all category names of yPicked
      this.catYValues = Object.values(
        this.categorizedData.find((e) => e.column === this.catYPicked)
          .categories
      ).slice(0, this.nCatToShow); // get array of all category counts of yPicked
      (this.catDatasets[0] = {
        label: this.catYPicked,
        data: this.catYValues,
        backgroundColor: this.catYValues.map(() => this.randomColor()),
      }),
        (this.catDataCollection = {
          labels: this.catChartValues,
          datasets: JSON.parse(JSON.stringify(this.catDatasets)).slice(
            0,
            this.nCatToShow
          ),
        });
    },
    updateRange() {
      this.fillNumPlotData();
    },
    // Called when user chooses or removes new Y-Lables
    updateXPicked(label) {
      this.xPicked = label;
    },
    updateYPicked(label) {
      if (this.datasets.map((e) => e.label).includes(label)) {
        let index = this.yPicked.indexOf(label);
        if (index > -1) {
          // only splice array when item is found
          this.yPicked.splice(index, 1); // 2nd parameter means remove one item only
        }
        this.removeChart(label);
      } else {
        this.yPicked.push(label);
        this.addSelectedChart(label);
      }
    },
    updateCatYPicked(label) {
      this.catYPicked = label;
    },
    addSelectedChart(label) {
      let yTmpValues = this.dataRows.map((e) => e[label]);
      const randomColor = this.randomColor();
      this.datasets.push({
        label: label,
        data: yTmpValues.slice(this.fromIndex - 1, this.toIndex),
        backgroundColor: randomColor,
        borderColor: randomColor,
        borderWidth: 1,
      });
      this.fillNumPlotData();
    },
    removeChart(label) {
      this.datasets = this.datasets.filter((o) => o.label !== label);
      this.fillNumPlotData();
    },
    randomColor() {
      const letters = "0123456789ABCDEF";
      let color = "#";
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    },
    async printChartAsPDF() {
      const canvasClass = this.activeTab == 'numerical' ? '.print-num-chart' : '.print-cat-chart';
      const printArea = this.$el.querySelector(canvasClass);
      if (!printArea) return;

      const canvas = await html2canvas(printArea, {
        backgroundColor: '#fff',
        scale: 2,
      });
      const imgData = canvas.toDataURL('image/png', 1);
      const imgProps = canvas;

      // Create a new PDF with increased height (50 pixels more)
      const increasedHeight = imgProps.height + 50; // Increase height by 50 pixels
      const pdf = new jsPDF('l', 'mm', [increasedHeight * 0.264583, imgProps.width * 0.264583]); // Convert pixels to mm

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate aspect ratio
      const aspectRatio = imgProps.width / imgProps.height;
      let imgWidth = pdfWidth;
      let imgHeight = pdfWidth / aspectRatio;

      // Adjust height if necessary
      if (imgHeight > pdfHeight) {
        imgHeight = pdfHeight;
        imgWidth = pdfHeight * aspectRatio;
      }

      // Calculate x position to center the image horizontally
      const xPosition = (pdfWidth - imgWidth) / 2;

      // Calculate y position to center the image vertically
      const yPosition = 10;

      pdf.addImage(imgData, 'PNG', xPosition, yPosition, imgWidth, imgHeight);

      // Add text at the bottom
      const text = "Consider reducing the size of the image to get a better resolution";
      const textYPosition = pdfHeight - 10; // Position 10mm from the bottom
      pdf.text(text, 10, textYPosition); // 10mm from the left

      pdf.save(`${this.title}_chart.pdf`);
    },
    onDirectPreviewLoaded() {
      this.clearDirectPreviewLoadTimeout();
      this.clearDirectPreviewInitTimeout();
      this.directPreviewLoading = false;
    },
    onDirectPreviewError() {
      this.clearDirectPreviewLoadTimeout();
      this.clearDirectPreviewInitTimeout();
      this.directPreviewUrl = '';
      this.directPreviewLoading = false;
      if (!this.directPreviewNotice) {
        this.directPreviewNotice = 'Inline preview is blocked by the source server. Please open the file in a new tab.';
      } else if (this.googleViewerFallbackUrl) {
        this.directPreviewNotice = 'Google Viewer fallback is currently unavailable for this file. Please open the file in a new tab.';
      }
    },
  },
  beforeUnmount() {
    this.cleanupDirectPreviewUrl();
  },
  watch: {
    xPicked() {
      this.$emit("input", this.xPicked);
      this.chartLabels = this.dataRows
        .map((e) => e[this.xPicked])
        .slice(this.fromIndex - 1, this.toIndex);
      this.xLabels = this.chartLabels;
      this.fillNumPlotData();
    },
    hideXLabels() {
      this.fillNumPlotData();
    },
    catViewType() {
      if (this.catViewType === "Pie") this.nCatToShow = 20;
      else if (this.catViewType === "Bar") this.nCatToShow = 50;
      else if (this.catViewType === "Line") this.nCatToShow = 100;
      // this.$emit("input", this.catYPicked);
      this.fillCatPlotData();
    },
    catYPicked() {
      this.$emit("input", this.catYPicked);
      this.fillCatPlotData();
    },
  },
};
</script>

<style lang="scss">
.dv-preview-container {
  /* padding: 3px; */
  /* border-bottom: 1px solid gray;
        border-top: 1px solid gray; */
  max-width: 100%;
  overflow: auto;
  overflow-y: hidden;
  // display: none;
}

.dv-preview-container .card-header {
  background-color: transparent;
}

.dv-preview-container .card {
  border: none;
}

.dv-nav-tabs li .nav-link {
  color: inherit;
  &.active {
    // background-color: oldlace;
    font-weight: bold;
    color: var(--primary);
    border-top: 4px solid var(--primary);
    margin-bottom: -2px;
  }
  &:hover:not(.active) {
    border-color: transparent;
  }
}

.dv-numtab-content canvas {
  max-width: 100%;
}

.dv-add-btn {
  height: 48px;
  align-self: end;
  background-color: var(--primary-light);
  color: white;

  &:hover {
    color: white;
  }
}

.dv-records-range-container {
  margin-right: 20px;
}

.dv-records-range-container input {
  text-align: center;
  width: 50px;
}

.dv-records-range-container-record-number {
  background-color: #f0efed;
  border-radius: 5px;
  padding: 10px 15px;
  font-weight: 650;
}

.dv-records-range-container small label {
  padding: 0 5px;
}

.dv-table-wrapper {
  max-width: 1108px;
  overflow: auto;
}

.dv-dist-table {
  margin: 15px 0;
}

.dv-dist-table td,
.dv-dist-table th {
  border: 1px solid #dfe2e5;
  padding: 0.6em 1em;
}

.dv-dist-table tr:nth-child(2n) {
  background-color: #f6f8fa;
}

.dv-feedback-tool-wrapper {
  padding: 5px 15px;
  margin: 15px 0;
  background-color: #f6f8fa;
  border: 1px solid #f6f8fa;
  border-radius: 5px;
}

.dv-canvas-container canvas {
  max-height: 500px;
}

.dv-direct-preview-container {
  width: 100%;
}

.dv-direct-preview-frame {
  border: 0;
}
</style>